import type { DiscoveryItemCode, TextSize } from '@/generated/graphql'
import { cn } from '@/shared/lib/utils'

interface TextPart {
  number: number
  text: string
  size: TextSize
}

interface DiscoveryCardProps {
  imageUrl: string
  content: TextPart[]
  code?: DiscoveryItemCode | null
  /** Defaults to the full scale the Discovery page draws at. */
  textScale?: DiscoveryCardTextScale
  onOpen: () => void
}

const TEXT_SIZE_MAP: Record<string, string> = {
  SMALL: 'text-[14px] font-semibold',
  STANDARD: 'text-[14px] font-semibold',
  BIG: 'text-[32px] font-semibold leading-tight',
}

/**
 * One step down the cover scale, for a card narrow enough that the full size would not fit — which
 * on Android is `DiscoveryCardTextScale.SMALL`, the scale `ProfileBonusMaterials` asks for. The
 * sizes are its coverM / coverS / coverXS verbatim, tracking included.
 */
const TEXT_SIZE_MAP_SMALL: Record<string, string> = {
  SMALL: 'text-[12px] leading-[12px] tracking-[-0.24px] font-semibold',
  STANDARD: 'text-[16px] leading-[16px] tracking-[-0.32px] font-semibold',
  BIG: 'text-[24px] leading-[24px] tracking-[0px] font-semibold',
}

export type DiscoveryCardTextScale = 'LARGE' | 'SMALL'

function isHorizontal(code?: DiscoveryItemCode | null) {
  return code === 'PRINTABLE_PLAN'
}

export function DiscoveryCard({
  imageUrl,
  content,
  code,
  textScale = 'LARGE',
  onOpen,
}: DiscoveryCardProps) {
  const horizontal = isHorizontal(code)
  const sorted = [...content].sort((a, b) => a.number - b.number)
  const sizeMap = textScale === 'SMALL' ? TEXT_SIZE_MAP_SMALL : TEXT_SIZE_MAP

  if (horizontal) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="relative h-[92px] w-full cursor-pointer overflow-hidden rounded-[24px] bg-app-grey-200 text-left transition-transform duration-200 ease-out hover:scale-[1.02] lg:h-[6.75rem]"
      >
        <img
          src={imageUrl}
          alt=""
          className="absolute left-0 object-cover object-left transition-opacity duration-300 scale-[102%] lg:inset-0 lg:h-full lg:w-full lg:object-[center_30%]"
          onLoad={(e) => e.currentTarget.classList.remove('opacity-0')}
        />
        <div className="relative flex h-full items-center pr-[24px] pt-[16px] pl-[32px]">
          <div className="flex flex-col gap-[8px]">
            {sorted.map((part) => (
              <span
                key={part.number}
                className={cn(
                  'text-app-grey-900 line-clamp-1',
                  sizeMap[part.size] ?? 'text-sm',
                )}
              >
                {part.text}
              </span>
            ))}
          </div>
        </div>
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className="relative h-[238px] w-full cursor-pointer overflow-hidden bg-app-grey-200 text-left transition-transform duration-200 ease-out hover:scale-[1.02] lg:h-[14.875rem]"
      style={{ borderRadius: '24px' }}
    >
      <img
        src={imageUrl}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-top opacity-0 transition-opacity duration-300"
        onLoad={(e) => e.currentTarget.classList.remove('opacity-0')}
      />
      <div className="relative flex h-full flex-col items-center justify-center p-[16px]">
        <div className="flex w-full flex-col items-center gap-[2px]">
          {sorted.map((part) => (
            <span
              key={part.number}
              className={cn(
                'w-full text-center text-app-grey-900',
                'hyphens-auto',
                sizeMap[part.size] ?? 'text-sm',
              )}
              style={{ overflowWrap: 'anywhere' }}
            >
              {part.text}
            </span>
          ))}
        </div>
      </div>
    </button>
  )
}
